/**
 * The editable grids behind the DSCR pricing sections — coupon ladders, LLPA
 * tables, tier margins and guideline overlays.
 *
 * All four edit the SAME `pricing` object and save through one PUT
 * (`/admin/data/dscr-pricing/{cpId}`), so this component holds a local draft of
 * the whole object and renders every requested section against it. That shared
 * draft is the reason the sections live in one component rather than four: the
 * endpoint replaces `pricing` wholesale, so four independent drafts would each
 * save a copy missing the others' edits.
 *
 * Ladder columns are discovered from the parsed rows rather than hardcoded — the
 * seeded Eastview sheet has `coupon` / `spread_-_5yr` / `5_1_arm` / `7_1_arm` /
 * `fixed_30`, but a new counterparty workbook can parse to a different set.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import PillSwitcher from "../shared/PillSwitcher";
import {
  ExpandableBlock,
  PRICING_GRID_COL_DEF,
  PricingField,
  PricingGrid,
  PricingSection,
  PricingSubSection,
  SectionEmpty,
  T,
} from "./pricingUi";

/** Anchor ids + rail labels for the DSCR sections, in render order. */
export const DSCR_SECTIONS = [
  { id: "dscr-ladder", label: "Coupon ladder" },
  { id: "dscr-llpa", label: "Adjustments (LLPA)" },
  { id: "dscr-margins", label: "Tier margins" },
  { id: "dscr-overlays", label: "Guideline overlays" },
];

const numericParser = (p) => {
  const v = String(p.newValue ?? "").trim();
  if (v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : p.oldValue;
};

const prettyColumn = (key) =>
  key
    .replace(/_-_/g, " ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

/* ── LLPA columns ─────────────────────────────────────────────────────────── */

/** 0.55 → "55%", 55 → "55%"; trailing zeros trimmed (0.675 → "67.5%"). */
const ltvPct = (n) => {
  const v = Number(n) <= 1 ? Number(n) * 100 : Number(n);
  return `${Number(v.toFixed(2))}%`;
};

/**
 * An LLPA table's values are a POSITIONAL array per row, and the parsed sheet
 * leaves spacer slots empty on every row — those were the columns rendering as a
 * full stack of dashes. The LTV cut boundaries ride along on the table's
 * `buckets`, one per POPULATED slot in ascending order, so dropping the dead
 * slots lines the two up and each column can carry the band the desk reads
 * instead of "#1 … #6".
 *
 * `slot` stays the ORIGINAL index — it's what an edit writes back into
 * `row.values`, which must keep its spacers to stay aligned with the sheet.
 */
const llpaColumns = (table) => {
  const rows = table?.rows || [];
  const width = Math.max(0, ...rows.map((r) => (r.values || []).length));
  const slots = Array.from({ length: width }, (_, i) => i).filter((i) =>
    rows.some((r) => {
      const v = (r.values || [])[i];
      return v != null && v !== "";
    }),
  );
  const cuts = (table?.buckets || [])
    .filter((b) => typeof b === "number")
    .sort((a, b) => a - b);

  return slots.map((slot, i) => ({
    slot,
    header:
      cuts[i] == null
        ? `#${i + 1}`
        : i === 0
          ? `LTV <= ${ltvPct(cuts[i])}`
          : `LTV > ${ltvPct(cuts[i - 1])} and <= ${ltvPct(cuts[i])}`,
  }));
};

/**
 * Fixed decimals so a column reads as one aligned stack and the parse's float
 * noise (0.007500000000000001) stays out of the UI. Strings pass through — "N/A"
 * is the sheet's marker for NOT PRICEABLE in that bucket, not zero.
 */
const llpaValue = (v) => {
  if (v == null || v === "") return "—";
  return typeof v === "number" ? (v * 100).toFixed(4) : String(v);
};

/**
 * The inverse of the ×100 display for a numeric-only column: the desk types what
 * it reads (102.0000, or 2.0000 for a rate) and the sheet stores the fraction
 * behind it (1.02 / 0.02). A stray `%` or comma is tolerated on the way in.
 */
const x100Parser = (p) => {
  const raw = String(p.newValue ?? "")
    .replace(/[%,\s]/g, "")
    .trim();
  if (raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? Number((n / 100).toFixed(8)) : p.oldValue;
};

/**
 * The inverse of `llpaValue` for edits: the desk types POINTS (1.3750) and the
 * sheet stores a fraction of price (0.01375). Non-numeric text passes through so
 * "N/A" can still be typed into a bucket that isn't priceable.
 */
const llpaParser = (p) => {
  const raw = String(p.newValue ?? "").trim();
  if (raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return Number((n / 100).toFixed(8));
};

export default function DscrPricingTables({ pricing, readOnly, version, onSave }) {
  const [draft, setDraft] = useState(() =>
    pricing ? structuredClone(pricing) : null,
  );
  const [dirty, setDirty] = useState(false);
  const [note, setNote] = useState("");
  const [tier, setTier] = useState("");

  // The draft is a CLONE, not the prop. The ladder and margin grids let AG Grid
  // write straight into their row objects, so sharing the prop's objects would
  // mutate the fetched document in place — and leave nothing to reset back to.
  const reseed = useCallback(() => {
    setDraft(pricing ? structuredClone(pricing) : null);
    setDirty(false);
    setNote("");
  }, [pricing]);

  useEffect(reseed, [reseed]);

  // The ladder + tier-margin grids bind directly to the draft's own row objects,
  // so AG Grid's default valueSetter writes the edit straight into `draft` —
  // marking dirty is all this component has to do. The LLPA / overlay grids take
  // the explicit clone-and-set path below because their edits land inside nested
  // arrays that AG Grid can't address with a plain field path.
  const ladders = useMemo(() => draft?.ladders || {}, [draft]);
  const tiers = useMemo(() => Object.keys(ladders), [ladders]);

  useEffect(() => {
    if (tiers.length && !tiers.includes(tier)) setTier(tiers[0]);
  }, [tiers, tier]);

  const save = useCallback(() => {
    if (!draft) return;
    onSave?.(draft, note || "edited in admin")
      .then(() => setDirty(false))
      .catch(() => {});
  }, [draft, note, onSave]);

  if (!draft) {
    return (
      <PricingSection id="dscr-ladder" title="Pricing">
        <SectionEmpty>
          This version has no parsed pricing objects. Import a workbook to
          populate it.
        </SectionEmpty>
      </PricingSection>
    );
  }

  /* ── Coupon ladders ─────────────────────────────────────────────────────── */
  const ladderRows = ladders[tier] || [];
  const ladderCounts = tiers.map((t) => ladders[t]?.length || 0);
  const showLadderCounts =
    ladderCounts.some((n) => n === 0) || new Set(ladderCounts).size > 1;
  const ladderColumns = Array.from(
    ladderRows.reduce((set, r) => {
      Object.keys(r).forEach((k) => set.add(k));
      return set;
    }, new Set()),
  );
  const orderedLadderColumns = [
    "coupon",
    ...ladderColumns.filter((c) => c !== "coupon"),
  ];

  const llpaTables = draft.llpaTables || [];
  const marginRows = draft.tierMargins || [];
  const overlays = draft.guidelineOverlays || [];

  return (
    <>
      <PricingSection
        id="dscr-ladder"
        title="Coupon ladder"
        desc="Bond prices per coupon for the selected tier, on the 100 scale (par = 100); coupon and spread are rates and read as percents. Columns come from the parsed workbook, so a new counterparty sheet can carry its own set."
        version={version}
        stackActions
        actions={
          tiers.length ? (
            <PillSwitcher
              items={tiers.map((t) => ({
                key: t,
                // The row count is a warning, not a label: shown only when a
                // tier parsed no rows, or its ladder length differs from the
                // others (normally every tier carries the same coupons).
                label: showLadderCounts
                  ? `${t} (${ladders[t]?.length || 0})`
                  : t,
              }))}
              value={tier}
              onChange={setTier}
            />
          ) : null
        }
        dense
      >
        {!ladderRows.length ? (
          <SectionEmpty>No ladder rows parsed for this tier.</SectionEmpty>
        ) : (
          <PricingGrid rowCount={ladderRows.length} maxRows={12}>
            <AgGridReact
              rowData={ladderRows}
              columnDefs={orderedLadderColumns.map((key) => {
                // EVERY ladder column is stored on the fraction scale and shown
                // ×100 to four decimals. The coupon (0.06125 — `monthly_pi`
                // divides it by 12) and the spread are RATES, so they carry a
                // percent sign; the rest are bond PRICES on the 100 scale, which
                // the min/max clamps confirm (97.25 / 104.75).
                const isCoupon = key === "coupon";
                const isRate = isCoupon || /spread/i.test(key);
                return {
                  headerName: prettyColumn(key),
                  field: key,
                  width: isCoupon ? 130 : undefined,
                  flex: isCoupon ? undefined : 1,
                  minWidth: isCoupon ? undefined : 130,
                  editable: !readOnly && !isCoupon,
                  cellClass: isCoupon ? "ctr" : "num",
                  headerClass: isCoupon ? "ctr" : "num",
                  valueParser: x100Parser,
                  valueFormatter: (p) => {
                    if (p.value == null || p.value === "") return "—";
                    if (typeof p.value !== "number") return String(p.value);
                    return `${(p.value * 100).toFixed(4)}${isRate ? "%" : ""}`;
                  },
                };
              })}
              defaultColDef={PRICING_GRID_COL_DEF}
              getRowId={(p) => `${tier}-${p.data.coupon}`}
              onCellValueChanged={() => setDirty(true)}
              stopEditingWhenCellsLoseFocus
            />
          </PricingGrid>
        )}
      </PricingSection>

      {/* ── LLPA tables ──────────────────────────────────────────────────── */}
      <PricingSection
        id="dscr-llpa"
        title="Adjustments (LLPA)"
        desc="Price adjustments stacked on the ladder, one column per LTV band from the sheet's cut boundaries. Values are bond points — par is 100, so a negative number prices wider."
        version={version}
        dense={!!llpaTables.length}
      >
        {!llpaTables.length ? (
          <SectionEmpty>No LLPA tables parsed on this version.</SectionEmpty>
        ) : (
          // Gap, not rules, separates one LLPA table from the next.
          <Flx column g={2}>
            {llpaTables.map((t, ti) => {
              const cols = llpaColumns(t);
              return (
                <PricingSubSection
                  key={t.table || ti}
                  title={t.table || `Table ${ti + 1}`}
                  meta={`${(t.rows || []).length} rows`}
                >
                  {/* The sub-section body already pads this one. */}
                  <PricingGrid
                    flush
                    rowCount={(t.rows || []).length}
                    maxRows={10}
                  >
                    <AgGridReact
                      rowData={(t.rows || []).map((r, ri) => ({
                        __ri: ri,
                        label: r.label,
                        ...Object.fromEntries(
                          (r.values || []).map((v, vi) => [`v${vi}`, v]),
                        ),
                      }))}
                      columnDefs={[
                        {
                          headerName: "Bucket",
                          field: "label",
                          minWidth: 220,
                          flex: 1,
                          pinned: "left",
                        },
                        // Share the leftover width rather than leaving a blank
                        // slab to the right of the last value column.
                        ...cols.map((c) => ({
                          headerName: c.header,
                          field: `v${c.slot}`,
                          flex: 1,
                          minWidth: 170,
                          editable: !readOnly,
                          cellClass: "num",
                          headerClass: "num",
                          valueParser: llpaParser,
                          valueFormatter: (p) => llpaValue(p.value),
                        })),
                      ]}
                      defaultColDef={{ ...PRICING_GRID_COL_DEF, sortable: false }}
                      getRowId={(p) => `${ti}-${p.data.__ri}`}
                      onCellValueChanged={(e) => {
                        const vi = Number(String(e.colDef.field).slice(1));
                        setDraft((prev) => {
                          const next = structuredClone(prev);
                          const row = next.llpaTables[ti].rows[e.data.__ri];
                          row.values = (row.values || []).slice();
                          row.values[vi] = e.newValue;
                          return next;
                        });
                        setDirty(true);
                      }}
                      stopEditingWhenCellsLoseFocus
                    />
                  </PricingGrid>
                </PricingSubSection>
              );
            })}
          </Flx>
        )}
      </PricingSection>

      {/* ── Tier margins ─────────────────────────────────────────────────── */}
      <PricingSection
        id="dscr-margins"
        title="Tier margins"
        desc="Points deducted from base ladder pricing per correspondent tier. Bond points, par = 100."
        version={version}
        dense
      >
        {!marginRows.length ? (
          <SectionEmpty>No tier margins parsed on this version.</SectionEmpty>
        ) : (
          <PricingGrid rowCount={marginRows.length} maxRows={8}>
            <AgGridReact
              rowData={marginRows}
              columnDefs={[
                { headerName: "Tier", field: "tier", minWidth: 200, flex: 1 },
                {
                  headerName: "Margin (pts)",
                  field: "margin",
                  width: 160,
                  editable: !readOnly,
                  cellClass: "num",
                  headerClass: "num",
                  valueParser: numericParser,
                  valueFormatter: (p) =>
                    p.value == null ? "—" : Number(p.value).toFixed(3),
                },
              ]}
              defaultColDef={PRICING_GRID_COL_DEF}
              getRowId={(p) => String(p.data.tier)}
              onCellValueChanged={() => setDirty(true)}
              stopEditingWhenCellsLoseFocus
            />
          </PricingGrid>
        )}

        {draft.minMaxPrices?.length ? (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Txt
              sx={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: T.ink,
                mb: 1,
              }}
            >
              Min / max prices
            </Txt>
            <ExpandableBlock collapsedMaxHeight={220} label="Show all">
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  bgcolor: T.surface2,
                  border: `1px solid ${T.line}`,
                  borderRadius: "8px",
                  fontSize: 11.5,
                  fontFamily: T.mono,
                }}
              >
                {JSON.stringify(draft.minMaxPrices, null, 2)}
              </Box>
            </ExpandableBlock>
          </Box>
        ) : null}
      </PricingSection>

      {/* ── Guideline overlays ───────────────────────────────────────────── */}
      <PricingSection
        id="dscr-overlays"
        title={`Guideline overlays (${overlays.length})`}
        desc="The guideline rules that gate or adjust leverage but aren't evident in the pricing matrix itself."
        version={version}
        actions={
          draft.overlayAgent?.agent ? (
            <Chip
              size="small"
              label={`mapped by ${draft.overlayAgent.agent}`}
              sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
            />
          ) : null
        }
        dense
      >
        {draft.overlayAgent?.notes ? (
          <Txt
            sx={{
              fontSize: 12.5,
              color: T.muted,
              display: "block",
              px: 2.5,
              pb: 1.5,
            }}
          >
            {draft.overlayAgent.notes}
          </Txt>
        ) : null}

        {!overlays.length ? (
          <SectionEmpty>No overlays mapped.</SectionEmpty>
        ) : (
          <PricingGrid rowCount={overlays.length} maxRows={10}>
            <AgGridReact
              rowData={overlays.map((o, i) => ({ ...o, __i: i }))}
              columnDefs={[
                {
                  headerName: "Rule",
                  field: "rule",
                  minWidth: 320,
                  flex: 2,
                  editable: !readOnly,
                  wrapText: true,
                  autoHeight: true,
                },
                {
                  headerName: "Kind",
                  field: "kind",
                  width: 150,
                  editable: !readOnly,
                },
                {
                  headerName: "Applies to",
                  field: "appliesTo",
                  minWidth: 160,
                  flex: 1,
                  editable: !readOnly,
                },
                {
                  headerName: "Suggested field",
                  field: "suggestedField",
                  minWidth: 170,
                  flex: 1,
                  editable: !readOnly,
                },
                {
                  headerName: "Value",
                  field: "value",
                  width: 120,
                  editable: !readOnly,
                  cellClass: "num",
                  headerClass: "num",
                  valueFormatter: (p) =>
                    p.value == null ? "—" : String(p.value),
                },
              ]}
              defaultColDef={PRICING_GRID_COL_DEF}
              getRowId={(p) => String(p.data.__i)}
              onCellValueChanged={(e) => {
                setDraft((prev) => {
                  const next = structuredClone(prev);
                  next.guidelineOverlays[e.data.__i][e.colDef.field] =
                    e.newValue;
                  return next;
                });
                setDirty(true);
              }}
              stopEditingWhenCellsLoseFocus
            />
          </PricingGrid>
        )}
      </PricingSection>

      {/* One save bar for the whole draft — every section above writes into the
          same `pricing` object and one PUT persists all of them. Sticks to the
          bottom of the column so it stays reachable down the long page. */}
      {readOnly || !dirty ? null : (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 5,
            bgcolor: T.surface,
            border: `1px solid ${T.warnLine}`,
            borderRadius: "10px",
            boxShadow: "0 -4px 16px -6px rgba(22,33,46,.14)",
          }}
        >
          {/* Aligned on the controls, not their labels — the commit field is two
              elements tall now, the text and buttons one. */}
          <Flx g={1.5} sx={{ p: 2, alignItems: "flex-end" }} wrap>
            <Txt
              sx={{
                fontSize: 12.5,
                fontWeight: 600,
                color: T.warn,
                whiteSpace: "nowrap",
                pb: 1,
              }}
            >
              Unsaved pricing edits
            </Txt>
            <PricingField
              label="Commit message"
              sx={{ minWidth: 240, flex: 1 }}
            >
              <TextField
                size="small"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
              />
            </PricingField>
            <Button variant="text" onClick={reseed}>
              Reset changes
            </Button>
            <Button variant="contained" onClick={save}>
              Save as a new version
            </Button>
          </Flx>
        </Box>
      )}
    </>
  );
}

DscrPricingTables.propTypes = {
  pricing: PropTypes.object,
  readOnly: PropTypes.bool,
  version: PropTypes.string,
  onSave: PropTypes.func,
};
